import { Application, Assets } from 'pixi.js';
// Ensure Spine-Pixi v8 loaders are registered with Pixi Assets
import '@esotericsoftware/spine-pixi-v8';
import type { GameOverReason, GameScene } from '@game/types';
import { createCharacterPrototypeScene } from '@game/scenes/CharacterPrototypeScene';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import {
	SPINEBOY_PRO_ATLAS_ASSET_ID,
	SPINEBOY_PRO_ATLAS_SRC,
	SPINEBOY_PRO_SKELETON_ASSET_ID,
	SPINEBOY_PRO_SKELETON_SRC
} from '@game/spineAssets';

const LOAD_STEPS = 6;
const LOAD_DELAY_MS = 120;

type LoadTask = (report: (value: number) => void) => Promise<void>;

export type GameEngineOptions = {
	createScene?: () => GameScene;
	loadTask?: LoadTask;
};

export class GameEngine {
	private app: Application | null = null;
	private container: HTMLElement | null = null;
	private scene: GameScene | null = null;
	private readonly createScene: () => GameScene;
	private readonly loadTask: LoadTask;
	private readonly inputDirection = { x: 0, y: 0 };
	private tuning: Tuning = { ...DEFAULT_TUNING };
	private onGameOver: ((reason: GameOverReason) => void) | null = null;
	private enemiesFrozen = false;
	private playerAnimOverride: import('@game/SpineAnimationController').AnimationState | null = null;

	constructor(options: GameEngineOptions = {}) {
		this.createScene = options.createScene ?? createCharacterPrototypeScene;
		this.loadTask = options.loadTask ?? simulateAssetLoad;
	}

	async loadAssets(onProgress?: (value: number) => void) {
		const report = onProgress ?? (() => undefined);

		// Load Spine assets
		report(10);
		try {
			// Register assets (safe to call multiple times; add() overwrites by id)
			Assets.add({ alias: SPINEBOY_PRO_SKELETON_ASSET_ID, src: SPINEBOY_PRO_SKELETON_SRC });
			Assets.add({ alias: SPINEBOY_PRO_ATLAS_ASSET_ID, src: SPINEBOY_PRO_ATLAS_SRC });

			// spine-pixi-v8 registers loaders that will resolve the atlas -> pngs automatically
			await Assets.load([SPINEBOY_PRO_SKELETON_ASSET_ID, SPINEBOY_PRO_ATLAS_ASSET_ID]);
			report(50);
		} catch (error) {
			console.error('Failed to load Spine assets, will use fallback cube:', error);
			// Store error flag
			Assets.cache.set('spineboy-pro-error', true);
			report(50);
		}

		// Continue with other assets
		await this.loadTask((value) => {
			report(50 + Math.round(value * 0.5));
		});
	}

	async start(container: HTMLElement) {
		await this.stop();
		this.container = container;
		const app = new Application();
		await app.init({
			resizeTo: container,
			antialias: true,
			autoDensity: true,
			background: '#f8fafc'
		});
		container.appendChild(app.canvas);
		app.ticker.add(this.handleTick);
		this.app = app;
		await this.mountScene();
	}

	async stop() {
		if (!this.app) return;
		await this.unmountScene();
		this.app.ticker.remove(this.handleTick);
		const canvas = this.app.canvas;
		this.app.destroy(true, { children: true });
		if (canvas && this.container?.contains(canvas)) {
			this.container.removeChild(canvas);
		}
		this.app = null;
		this.container = null;
	}

	async resetScene() {
		if (!this.app) return;
		await this.unmountScene();
		await this.mountScene();
	}

	setInputDirection(x: number, y: number) {
		this.inputDirection.x = x;
		this.inputDirection.y = y;
		this.scene?.setInputDirection?.(x, y);
	}

	triggerFire() {
		this.scene?.triggerFire?.();
	}

	triggerDash(x: number, y: number) {
		this.scene?.triggerDash?.(x, y);
	}

	setOnGameOver(handler: ((reason: GameOverReason) => void) | null) {
		this.onGameOver = handler;
		this.scene?.setOnGameOver?.(handler);
	}

	setEnemiesFrozen(frozen: boolean) {
		this.enemiesFrozen = frozen;
		this.scene?.setEnemiesFrozen?.(frozen);
	}

	setPlayerAnimationOverride(state: import('@game/SpineAnimationController').AnimationState | null) {
		this.playerAnimOverride = state;
		this.scene?.setPlayerAnimationOverride?.(state);
	}

	restartPlayerAnimationOverride() {
		this.scene?.restartPlayerAnimationOverride?.();
	}

	setTuning(patch: Partial<Tuning>) {
		this.tuning = { ...this.tuning, ...patch };
		this.scene?.setTuning?.(patch);
	}

	getTuning(): Tuning {
		return this.tuning;
	}

	private async mountScene() {
		if (!this.app) return;
		this.scene = this.createScene();
		await this.scene.init(this.app);
		this.scene.setInputDirection?.(this.inputDirection.x, this.inputDirection.y);
		this.scene.setTuning?.(this.tuning);
		this.scene.setOnGameOver?.(this.onGameOver);
		this.scene.setEnemiesFrozen?.(this.enemiesFrozen);
		this.scene.setPlayerAnimationOverride?.(this.playerAnimOverride);
	}

	private async unmountScene() {
		if (!this.scene) return;
		await this.scene.dispose();
		this.scene = null;
	}

	private handleTick = (ticker: { deltaMS: number }) => {
		const delta = ticker.deltaMS / 1000;
		if (!delta) return;
		this.scene?.update(delta);
	};
}

function simulateAssetLoad(report: (value: number) => void): Promise<void> {
	return new Promise(async (resolve) => {
		for (let step = 1; step <= LOAD_STEPS; step += 1) {
			await delay(LOAD_DELAY_MS);
			report(Math.round((step / LOAD_STEPS) * 100));
		}
		resolve();
	});
}

function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
